import { __decorate } from "tslib";
import { Group, Vector3 } from 'three';
import { FullAnalyzerAPConfig as Config } from '../../Config/FullAnalyzerAPConfig.js';
import { LabelTextConfig } from '../../Config/LabeltextConfig.js';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { ToolType } from '../../Core/ITool.js';
import { SaveChild } from '../../Core/Save.js';
import { ViewType } from '../../Models/AppModel.js';
import { BoneTypeEnum } from '../../Models/CaseModel.js';
import { SelectedApexMech } from '../../States/State.js';
import { Consts } from '../../Utils/Consts.js';
import { Default } from '../../Utils/Default.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { CalculationPoint } from '../Primitive/CalculationPoint.js';
import { LabelText } from '../Primitive/Label/LabelText.js';
import { LinePointToPoint2 } from '../Primitive/LinePointToPoint2.js';
import { MechanicalAxisFemurAP } from './FemurAnalyzerAP.js';
import { MechanicalAxisTibiaAP } from './TibiaAnalyzerAP.js';
export class MechanicalAxisAP extends Group {
    onAfterRestore() {
        this.dispatchEvent({ type: 'onAfterRestore' });
    }
    onSelect() {
        Default.colorAllDraggablePoints(this);
    }
    onDeselect() {
        Default.restoreColorAllDraggablePoints(this);
    }
    constructor(isLeft, boneType, isContralateral, isEOC) {
        super();
        this.isSelectable = true;
        this.isDeletable = false;
        this.isSelected = false;
        this.toolType = isContralateral ? ToolType.contralateralMechanicalAxis : ToolType.mechanicalAxis;
        this.name = isContralateral ? 'FullAnalyzer_Contralateral' : 'FullAnalyzer';
        this.boneType = boneType;
        this.isLeft = isLeft;
        this._isContralateral = isContralateral;
        this.addEventListener('historyChanged', () => {
            this.dispatchEvent({ type: 'updated' });
        });
        if (boneType === BoneTypeEnum.Femur || boneType === BoneTypeEnum.LongLeg) {
            this.add(this.femur = new MechanicalAxisFemurAP(isLeft, isContralateral, isEOC));
            this.femur.addEventListener('updated', () => { this.dispatchEvent({ type: 'updated' }); });
        }
        if (boneType === BoneTypeEnum.Tibia || boneType === BoneTypeEnum.LongLeg) {
            this.add(this.tibia = new MechanicalAxisTibiaAP(isLeft, isContralateral, isEOC));
            this.tibia.addEventListener('updated', () => { this.dispatchEvent({ type: 'updated' }); });
        }
        if (boneType === BoneTypeEnum.LongLeg) {
            this.add(this.tibiaDistancePoint = new CalculationPoint('tibiaDistancePoint'), this.weightBearing = new LinePointToPoint2('weightBearing', ViewType.AP, Config.weightBearing_color, this.femur.mechanical.FH, this.tibia.CA));
            this.femur.setTibiaLimits(this.tibia.LP.position, this.tibia.MP.position);
            this.tibia.setFemurLimits(this.femur.mechanical.LE.position, this.femur.mechanical.ME.position);
            this.createCachedMeasure();
        }
        if (isContralateral) {
            const objToAnchor = this.femur ? this.femur.mechanical.CE : this.tibia.CP;
            this.add(this._nameLabel = new LabelText({
                name: 'nameLabel',
                color: LabelTextConfig.color,
                viewType: ViewType.AP,
                objToAnchor,
                anchor: new Vector3(15),
                labelParams: { text: 'Contralateral', backgroundColor: Config.nameLabel_backgroundColor, fontColor: Config.nameLabel_fontColor }
            }));
        }
    }
    getAngleForTemplating(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.angleForTemplating : this.tibia.angleForTemplating;
    }
    getApexPosition(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.mechanical.apex.position : this.tibia.apex.position;
    }
    getProximalDirToCalculateApex(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.proximalDirToCalculateApex : this.tibia.proximalDirToCalculateApex;
    }
    getDistalDirToCalculateApex(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.distalDirToCalculateApex : this.tibia.distalDirToCalculateApex;
    }
    getApexWithSign(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.apexWithSign : this.tibia.apexWithSign;
    }
    getApexAngle(selectedApex) {
        return selectedApex === SelectedApexMech.femurDistal || selectedApex == SelectedApexMech.femurProximal ? this.femur.apexAngle.value : this.tibia.apexAngle.value;
    }
    get cutPoint() {
        if (this.tibia) {
            return this.tibia.CA.position.clone().add(this.tibia.CA.position.clone().sub(this.tibia.CP.position).setLength(100000));
        }
        else {
            return this.femur.mechanical.CE.position.clone().add(this.femur.mechanical.CE.position.clone().sub(this.femur.mechanical.FH.position).setLength(100000));
        }
    }
    getPoints() {
        const points = [];
        this.femur && points.push(this.femur.mechanical.GT.position.clone(), this.femur.mechanical.FH.position.clone(), this.femur.mechanical.LE.position.clone(), this.femur.mechanical.ME.position.clone(), this.femur.anatomical.NS_FH_A.position.clone(), this.femur.anatomical.NS_FH_B.position.clone(), this.femur.anatomical.NS_GT_lower_A.position.clone(), this.femur.anatomical.NS_GT_lower_B.position.clone(), this.femur.anatomical.NS_GT_upper_A.position.clone(), this.femur.anatomical.NS_GT_upper_B.position.clone());
        this.tibia && points.push(this.tibia.LP.position.clone(), this.tibia.MP.position.clone(), this.tibia.LA.position.clone(), this.tibia.MA.position.clone());
        return points;
    }
    restorePoints(points) {
        this.femur?.restorePositions(points.splice(0, 10));
        this.tibia?.restorePositions(points);
    }
    createCachedMeasure() {
        this.JLCA = new CachedMeasure(() => {
            return this.femur.mechanical.LE.position.clone().sub(this.femur.mechanical.ME.position).angleTo(this.tibia.LP.position.clone().sub(this.tibia.MP.position));
        }, this.femur.mechanical.LE, this.femur.mechanical.ME, this.tibia.LP, this.tibia.MP);
        this.MAD = new CachedMeasure(() => {
            return this.tibia.CP.position.distanceTo(VectorUtils.projectOnVector(this.tibia.CP.position, this.weightBearing.v1.position, this.weightBearing.v2.position));
        }, this.tibia.CP, this.femur.mechanical.FH, this.tibia.CA);
        this.length = new CachedMeasure(() => {
            return this.femur.mechanical.FH.position.distanceTo(this.tibia.CA.position);
        }, this.femur.mechanical.FH, this.tibia.CA);
        this.kneeTranslation = new CachedMeasure(() => {
            const weightBearingDir = this.femur.mechanical.FH.position.clone().sub(this.tibia.CA.position);
            const perpDir = VectorUtils.getPerpendicular(weightBearingDir);
            const tibiaDistancePoint = VectorUtils.lines2DIntersection(this.femur.mechanical.CE.position, this.femur.mechanical.CE.position.clone().add(perpDir), this.tibia.CP.position, this.tibia.CP.position.clone().add(weightBearingDir));
            if (tibiaDistancePoint) {
                this.tibiaDistancePoint.position.copy(tibiaDistancePoint);
                return this.femur.mechanical.CE.position.distanceTo(tibiaDistancePoint);
            }
            return undefined;
        }, this.femur.mechanical.FH, this.femur.mechanical.CE, this.tibia.CP, this.tibia.CA);
    }
    get pointsToRegister() {
        return (this.femur ? this.femur.pointsToRegister : 0) + (this.tibia ? this.tibia.pointsToRegister : 0);
    }
    handleRegisteredPoints(points) {
        if (this.arePointsValid(points)) {
            this.femur?.initPositions(points.splice(0, 4));
            this.tibia?.initPositions(points);
            return true;
        }
        return false;
    }
    arePointsValid(points) {
        let index = 0;
        const bones = this.boneType === BoneTypeEnum.LongLeg ? [this.femur, this.tibia] : (this.boneType === BoneTypeEnum.Femur ? [this.femur] : [this.tibia]);
        for (const bone of bones) {
            if (Math.min(points[index].y, points[index + 1].y) <= Math.max(points[index + 2].y, points[index + 3].y)) {
                return false;
            }
            index += bone.pointsToRegister;
        }
        if (this.boneType === BoneTypeEnum.LongLeg) {
            return !VectorUtils.arePointsOnSameSide(points[2], points[3], Consts.planeNormal, points[0], points[4], points[5]);
        }
        return true;
    }
    setSelectedApex(value) {
        if (this.femur) {
            this.femur.selectedApex = value;
        }
        if (this.tibia) {
            (this.tibia).selectedApex = value;
        }
    }
}
__decorate([
    SaveChild()
], MechanicalAxisAP.prototype, "femur", void 0);
__decorate([
    SaveChild()
], MechanicalAxisAP.prototype, "tibia", void 0);
__decorate([
    SaveChild()
], MechanicalAxisAP.prototype, "_nameLabel", void 0);
//# sourceMappingURL=FullAnalyzerAP.js.map